import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  SkeletonCircle,
  SkeletonText,
  useToast,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLatestJobListAsync,
  selectLatestJobList,
} from "./LatestJobListSlice";
import { Link, useNavigate } from "react-router-dom";
import { TimeDifference } from "../../common/TimeDifference";
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../../common/URLs";
// import { NoDataFound } from "../path/to/NoDataFound"; // Update with the correct path
import {
  MapPinIcon,
  ClockIcon,
  HeartIcon,
  CurrencyRupeeIcon,
  BookmarkIcon,
  BookmarkSlashIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";
import { NoDataFound } from "../../../pages/CommonPublicPages/Basic/NoDataFound";
import { updateBookmarkAsync } from "../../candidateDashboard/candidateSlice/SavedJobs/SavedJobsSlice";
import { fetchUserRole } from "../../common/FetchUserRole";

function LatestJobList() {
  const dispatch = useDispatch();
  const jobs = useSelector(selectLatestJobList);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchLatestJobListAsync())
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  // Function to render job content based on loading and data state
  const renderJobContent = () => {
    if (isLoading) {
      // Show skeleton loader when data is being fetched
      return (
        <div className="col-span-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Box
              key={index}
              width="full"
              padding="6"
              boxShadow="sm"
              bg="white"
              mb="4"
            >
              <SkeletonCircle size="10" />
              <SkeletonText
                mt="4"
                noOfLines={4}
                spacing="4"
                skeletonHeight="2"
              />
            </Box>
          ))}
        </div>
      );
    } else if (!Array.isArray(jobs) || jobs.length === 0) {
      // Show NoDataFound component when there's no data
      return (
        <div className="col-span-2">
          <NoDataFound
            title="No Job Listings Found"
            message="We couldn't find any job listings at the moment. Please check back later."
          />
        </div>
      );
    } else {
      // Show actual job data
      return jobs.map((job) => (
        <div
          key={job.uuid}
          className="bg-white rounded-xl border border-gray-100 relative hover:shadow-sm transition-shadow"
        >
          {/* Bookmark Button */}

          {/* Main Content */}
          <div className="flex items-start gap-3 px-3 py-2 border-b border-b-gray-200">
            {/* Company Logo */}
            <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-50">
              {job?.company_logo ? (
                <img
                  className="w-full h-full object-cover"
                  src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job?.company_logo}`}
                  alt={job.company_name}
                />
              ) : (
                <Avatar
                  name={job.company_name}
                />
              )}
            </div>

            {/* Job Info */}
            <div className="flex-1 space-y-0 min-w-0">
              <div className="font-semibold text-xl text-gray-900 truncate">
                {job.job_title?.title}
              </div>
              <p className="text-sm text-blue-600 font-medium ">
                {job.job_name || "N/A"}
              </p>
            </div>

            {/* Bookmark Icon */}
            <div className="flex flex-col items-end justify-between">
              <button
                onClick={() => handleBookmarkClick(job.uuid)}
                className="bookmark"
              >
                {bookmarkedJobs[job.uuid] ? (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="black"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 3C4.9 3 4 3.9 4 5V21L12 16.5L20 21V5C20 3.9 19.1 3 18 3H6Z"
                      stroke="black"
                      strokeWidth="2"
                    />
                  </svg>
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 3C4.9 3 4 3.9 4 5V21L12 16.5L20 21V5C20 3.9 19.1 3 18 3H6Z"
                      stroke="black"
                      strokeWidth="2"
                    />
                  </svg>
                )}
              </button>
              {appliedJobs[job.uuid] && (
                <span className="text-green-600 text-sm font-semibold flex items-center gap-1">
                  ✅ Applied
                </span>
              )}
            </div>
          </div>

          {/* Job Details */}
          <div className="mt-2 space-y-1 px-3">
            <div className="flex items-center text-gray-500 text-sm">
              <BriefcaseIcon className="h-4 w-4 mr-1" />
              <span>{job.company_name}</span>
            </div>
            {/* Location */}
            <div className="flex items-center text-gray-500 text-sm">
              <MapPinIcon className="h-4 w-4 mr-1" />
              <span>
                {job.job_locations
                  .slice(0, 5)
                  .map((loc) => loc.job_location?.title)
                  .join(", ")}
                {job.job_locations.length > 4 &&
                  ` +${job.job_locations.length - 5} more`}
              </span>
            </div>



            {/* Salary */}
            <div className="flex items-center text-gray-500 text-sm">
              <CurrencyRupeeIcon className="h-4 w-4 mr-1" />
              <span>
                {job.salary_min_amount &&
                  job.salary_max_amount &&
                  job.salary_min_amount !== 0 &&
                  job.salary_max_amount !== 0
                  ? `${job.salary_min_amount} - ${job.salary_max_amount}/${job.salary_per_type}`
                  : "Not Disclosed"}
              </span>
            </div>
          </div>

          {/* Details Button */}
          <div className="my-2 flex items-center justify-between px-3">
            <div className="flex items-center text-gray-500 text-sm">
              <ClockIcon className="h-4 w-4 mr-1 " />
              <span>
                <TimeDifference dateTimeString={job.published_at} />
              </span>
            </div>
            <Link to={`/jobs/${job.uuid}`} className="">
              <button className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-emerald-600 bg-emerald-50 hover:bg-emerald-100 transition-colors">
                Details
              </button>
            </Link>
          </div>
        </div>
      ));
    }
  };

  const [bookmarkedJobs, setBookmarkedJobs] = useState({});

  const [appliedJobs, setAppliedJobs] = useState({});

  // Prefill applied jobs state when jobs are loaded
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialAppliedJobs = {};
      jobs.forEach((job) => {
        initialAppliedJobs[job.uuid] = job.candidate_job_apply_count === 1;
      });
      setAppliedJobs(initialAppliedJobs);
    }
    window.scrollTo(0, 0);
  }, [jobs]);

  // Prefill bookmark state when jobs are loaded
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialBookmarks = {};
      jobs.forEach((job) => {
        initialBookmarks[job.uuid] = job.candidate_job_saved_count
          ? true
          : false;
      });
      setBookmarkedJobs(initialBookmarks);
    }
    window.scrollTo(0, 0);
  }, [jobs]); // Run when jobs updates

  const toast = useToast();
  const navigate = useNavigate();

  const handleBookmarkClick = async (jobId) => {
    // First check the role and handle redirection if needed
    try {
      // Convert the callback-based fetchUserRole to a Promise
      const role = await new Promise((resolve, reject) => {
        fetchUserRole((role) => {
          resolve(role);
        });
      });

      // If not a candidate, redirect and exit the function
      if (role !== "candidate") {
        toast({
          title: "Please login first",
          description: "You need to login as a candidate to save or apply for a job.",
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        navigate("/login");
        return; // Stop further execution
      }

      // Only proceed with bookmark if user is a candidate
      const isCurrentlyBookmarked = bookmarkedJobs[jobId]; // Check current status
      const newStatus = isCurrentlyBookmarked ? "remove" : "save"; // Toggle between "save" and "remove"

      // Optimistic UI update
      setBookmarkedJobs((prev) => ({
        ...prev,
        [jobId]: !isCurrentlyBookmarked,
      }));

      await dispatch(
        updateBookmarkAsync({ job_uuid: jobId, status: newStatus })
      );

      // Show success toast
      toast({
        title: `Job ${newStatus === "save" ? "saved" : "removed"
          } successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Failed to update bookmark:", error);

      // Revert on failure - only if we got past the role check
      if (bookmarkedJobs[jobId] !== undefined) {
        setBookmarkedJobs((prev) => ({
          ...prev,
          [jobId]: !prev[jobId], // Revert to previous state
        }));
      }

      // Show error toast
      toast({
        title: "Failed to update bookmark.",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  return (
    <div className="sm:max-w-7xl mx-auto mb-12 sm:p-1 ">
      <div className="mt-5 mb-3 flex flex-wrap sm:justify-between justify-center px-8 sm:px-1">
        <div>
          <h2 className="py-2 font-semibold flex justify-center gap-2 sm:justify-start text-4xl  text-gray-900">
            <span className="text-fontBlue">Latest</span> Job Posts
          </h2>
          <h6 className="text-gray-600 font-normal">
            Know your worth and find the job that qualifies your life
          </h6>
        </div>
        <div className="flex justify-center sm:justify-start">
          <Link to={"/jobs"}>
            <button className="text-blue-600 text-lg ">
              Browse all jobs →
            </button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:px-4 px-2">
        {renderJobContent()}
      </div>
    </div>
  );
}

export default LatestJobList;
